import { App } from '@capacitor/app'
import i18next from 'i18next'
import { useEffect } from 'react'

import { isNative } from '@/shared/api/platform'
import { notify } from '@/shared/lib/notify'

/** Ventana del "doble atrás para salir" en la raíz. */
const DOUBLE_BACK_MS = 2000

/**
 * Botón atrás nativo de Android (solo app). Sin esto, el botón/gesto atrás cae
 * al comportamiento por defecto de la Activity = salir de la app en cada toque.
 *
 * <p>Aquí lo cableamos a la navegación de la SPA:
 * <ul>
 *   <li>Si hay historial (`canGoBack`) → vuelve a la pantalla anterior
 *       (`history.back()`; `createBrowserRouter` reacciona al `popstate`).</li>
 *   <li>En la raíz (sin historial) → "pulsa atrás otra vez para salir": el primer
 *       toque avisa con un toast; un segundo dentro de {@link DOUBLE_BACK_MS}
 *       cierra la app.</li>
 * </ul>
 * En web es no-op (no hay botón atrás nativo). Se monta una vez desde `App`.
 */
export function useAndroidBackButton(): void {
  useEffect(() => {
    if (!isNative()) return
    let lastBackAt = 0
    const handlePromise = App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back()
        return
      }
      const now = Date.now()
      if (now - lastBackAt < DOUBLE_BACK_MS) {
        void App.exitApp()
      } else {
        lastBackAt = now
        notify.info(i18next.t('toasts.backToExit'), { duration: DOUBLE_BACK_MS })
      }
    })
    return () => {
      void handlePromise.then((handle) => handle.remove())
    }
  }, [])
}
